import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminListAuthors,
  adminUpsertAuthor,
  adminDeleteAuthor,
  adminSetDefaultAuthor,
} from "@/lib/authors.functions";

export const Route = createFileRoute("/admin/authors")({
  component: AuthorsAdmin,
});

type AuthorRow = {
  id: string;
  slug: string;
  name: string;
  bio: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  website_url: string | null;
  avatar_url: string | null;
  is_default: boolean;
};

type FormState = {
  id?: string;
  name: string;
  slug: string;
  bio: string;
  linkedin_url: string;
  twitter_url: string;
  website_url: string;
  avatar_url: string;
  is_default: boolean;
};

const EMPTY: FormState = {
  name: "",
  slug: "",
  bio: "",
  linkedin_url: "",
  twitter_url: "",
  website_url: "",
  avatar_url: "",
  is_default: false,
};

const input = "w-full border border-border bg-background px-3 py-2 text-sm";

function AuthorsAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "authors"],
    queryFn: () => adminListAuthors(),
  });
  const authors = (data ?? []) as AuthorRow[];

  const [editing, setEditing] = useState<FormState | null>(null);

  const save = useMutation({
    mutationFn: (f: FormState) =>
      adminUpsertAuthor({
        data: {
          id: f.id,
          name: f.name,
          slug: f.slug || undefined,
          bio: f.bio,
          linkedin_url: f.linkedin_url || null,
          twitter_url: f.twitter_url || null,
          website_url: f.website_url || null,
          avatar_url: f.avatar_url || null,
          is_default: f.is_default,
        },
      }),
    onSuccess: () => {
      toast.success("Saved");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin", "authors"] });
      qc.invalidateQueries({ queryKey: ["authors", "public"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const del = useMutation({
    mutationFn: (id: string) => adminDeleteAuthor({ data: { id } }),
    onSuccess: () => {
      toast.success("Author deleted");
      qc.invalidateQueries({ queryKey: ["admin", "authors"] });
      qc.invalidateQueries({ queryKey: ["authors", "public"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => adminSetDefaultAuthor({ data: { id } }),
    onSuccess: () => {
      toast.success("Default author updated");
      qc.invalidateQueries({ queryKey: ["admin", "authors"] });
      qc.invalidateQueries({ queryKey: ["authors", "public"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="headline-display text-3xl">Authors</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Bylines shown on articles and on the About page. Article author is a free-text field on
            each post; matching by name pulls in the bio and social links below.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY })}
          className="bg-foreground text-background px-4 py-2 text-sm font-medium hover:bg-ochre-deep transition-colors"
        >
          + New author
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : authors.length === 0 ? (
        <p className="text-sm text-muted-foreground">No authors yet.</p>
      ) : (
        <div className="border border-border">
          <table className="w-full text-sm">
            <thead className="bg-paper-soft text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Slug</th>
                <th className="text-left px-3 py-2">Default</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {authors.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{a.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.slug}</td>
                  <td className="px-3 py-2">
                    {a.is_default ? (
                      <span className="text-xs bg-foreground text-background px-2 py-0.5">
                        Default
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDefault.mutate(a.id)}
                        disabled={setDefault.isPending}
                        className="text-xs text-ochre-deep hover:underline"
                      >
                        Set default
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right space-x-3">
                    <button
                      type="button"
                      className="text-xs underline hover:text-accent"
                      onClick={() =>
                        setEditing({
                          id: a.id,
                          name: a.name,
                          slug: a.slug,
                          bio: a.bio ?? "",
                          linkedin_url: a.linkedin_url ?? "",
                          twitter_url: a.twitter_url ?? "",
                          website_url: a.website_url ?? "",
                          avatar_url: a.avatar_url ?? "",
                          is_default: a.is_default,
                        })
                      }
                    >
                      Edit
                    </button>
                    {!a.is_default && (
                      <button
                        type="button"
                        className="text-xs underline text-destructive hover:text-destructive/80"
                        onClick={() => {
                          if (window.confirm(`Delete author "${a.name}"?`)) del.mutate(a.id);
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AuthorEditor
          value={editing}
          onCancel={() => setEditing(null)}
          onSave={(f) => save.mutate(f)}
          saving={save.isPending}
        />
      )}
    </div>
  );
}

function AuthorEditor({
  value,
  onCancel,
  onSave,
  saving,
}: {
  value: FormState;
  onCancel: () => void;
  onSave: (f: FormState) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<FormState>(value);
  useEffect(() => setForm(value), [value]);

  return (
    <div className="fixed inset-0 bg-foreground/40 z-50 flex items-start justify-center overflow-y-auto py-10">
      <div className="w-full max-w-2xl bg-background border-2 border-foreground p-6 mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="headline-display text-2xl">
            {form.id ? "Edit author" : "New author"}
          </h2>
          <button onClick={onCancel} className="text-sm text-muted-foreground hover:text-foreground">
            Cancel
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(form);
          }}
          className="space-y-4"
        >
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Name</label>
            <input
              className={input}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Slug (URL anchor on /about)
            </label>
            <input
              className={input}
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="auto from name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Bio</label>
            <textarea
              rows={6}
              className={input}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="A paragraph or two — background, beat, why readers should trust this byline."
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                LinkedIn URL
              </label>
              <input
                className={input}
                value={form.linkedin_url}
                onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                placeholder="https://www.linkedin.com/in/…"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Twitter / X URL
              </label>
              <input
                className={input}
                value={form.twitter_url}
                onChange={(e) => setForm({ ...form, twitter_url: e.target.value })}
                placeholder="https://x.com/…"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Website URL
              </label>
              <input
                className={input}
                value={form.website_url}
                onChange={(e) => setForm({ ...form, website_url: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Avatar URL
              </label>
              <input
                className={input}
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            />
            Set as default author (used when an article has no matching byline)
          </label>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="bg-foreground text-background px-4 py-2 text-sm font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save author"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="border border-border px-4 py-2 text-sm hover:bg-paper-soft transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
